// SPDX-License-Identifier: MIT
// Generated by scripts/browser.ts from ../brand.ts. Do not edit.
/**
 * Brand pack (§7.5.1). Extraction fills it from the site; users may edit it (FR-4.6). Every number
 * or claim a reel shows must come from `facts[]`, each with its source (FR-4.5).
 */
import * as z from 'zod/mini';
const hex = /*@__PURE__*/ (() => z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/, 'must be #RRGGBB')))();
export const BrandSchema = /*@__PURE__*/ (() =>
  z
    .object({
      schema: z.literal('pulsereel.brand/1'),
      name: z.string().check(z.minLength(1)).check(z.maxLength(80)),
      url: z.optional(z.string()),
      palette: z
        .array(z.object({ role: z.enum(['primary', 'accent', 'ink', 'paper']), hex }))
        .check(z.minLength(1)),
      fonts: z.object({
        display: z._default(z.string(), 'Inter'),
        cjk: z._default(z.string(), 'Noto Sans SC'),
        mono: z._default(z.string(), 'JetBrains Mono'),
      }),
      languages: z.optional(
        z.array(z.string().check(z.minLength(2)).check(z.maxLength(16))).check(z.maxLength(8)),
      ),
      copy: z._default(
        z.object({
          tagline: z.optional(z.string().check(z.maxLength(120))),
          headline: z.optional(z.string().check(z.maxLength(200))),
          subhead: z.optional(z.string().check(z.maxLength(400))),
          keywords: z.optional(z.array(z.string().check(z.maxLength(60))).check(z.maxLength(20))),
        }),
        {},
      ),
      /** Verifiable facts. A reel may only show numbers that appear in some `value` here. */
      facts: z._default(
        z
          .array(
            z.object({
              id: z.string().check(z.regex(/^[a-z0-9_-]{1,32}$/i)),
              value: z.string().check(z.minLength(1)).check(z.maxLength(40)),
              label: z.string().check(z.minLength(1)).check(z.maxLength(200)),
              source: z
                .object({
                  url: z.optional(z.string().check(z.maxLength(2000))),
                  page: z.optional(z.number().check(z.int()).check(z.positive())),
                  quote: z.string().check(z.minLength(1)).check(z.maxLength(500)),
                })
                .check(
                  z.refine(
                    (s) => s.url !== undefined || s.page !== undefined,
                    'a source needs a url or a page',
                  ),
                ),
            }),
          )
          .check(z.maxLength(100)),
        [],
      ),
      /**
       * The brand's logo as vector paths (from the site's inline SVG or an uploaded SVG, after
       * sanitising). Scenes animate the real outlines: draw-on, fills, point assemblies.
       */
      logo: z.optional(
        z.object({
          viewBox: z.tuple([
            z.number(),
            z.number(),
            z.number().check(z.positive()),
            z.number().check(z.positive()),
          ]),
          paths: z
            .array(
              z.object({
                d: z.string().check(z.minLength(1)).check(z.maxLength(200000)),
                fill: z.optional(hex),
                role: z.optional(z.string()),
                /** SVG fill-rule. Outlines with counters (O, D, 0) usually need "evenodd". */
                fillRule: z.optional(z.enum(['nonzero', 'evenodd'])),
              }),
            )
            .check(z.minLength(1))
            .check(z.maxLength(200)),
        }),
      ),
    })
    .check(
      z.superRefine((brand, ctx) => {
        const seen = new Set<string>();
        brand.facts.forEach((f, i) => {
          if (seen.has(f.id))
            ctx.addIssue({
              code: 'custom',
              path: ['facts', i, 'id'],
              message: `duplicate fact id "${f.id}"`,
            });
          seen.add(f.id);
        });
      }),
    ))();
export type Brand = z.infer<typeof BrandSchema>;
/** A brand pack as written (defaults not yet applied), e.g. straight from brand.json. */
export type BrandInput = z.input<typeof BrandSchema>;
